(() => {
  const STORAGE_KEY = 'qls-active-cart-offer-v1';

  function route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
  }

  function readPrimaryOffer(node) {
    return {
      code: String(node.dataset.code || '').trim(),
      minimumCents: Math.max(0, Number(node.dataset.minimumCents || 0)),
      savingsText: node.dataset.savingsText || 'Special offer',
      endAt: node.dataset.endAt || '',
      storedAt: Date.now()
    };
  }

  function readButtonOffer(button, node) {
    return {
      code: String(button.dataset.code || '').trim(),
      minimumCents: Math.max(0, Number(button.dataset.minimumCents || 0)),
      savingsText: button.dataset.savingsText || node.dataset.savingsText || 'Special offer',
      endAt: node.dataset.endAt || '',
      storedAt: Date.now()
    };
  }

  function parseEnd(value) {
    if (!value) return null;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  function isExpired(offer) {
    const end = parseEnd(offer.endAt);
    return end !== null && Date.now() >= end;
  }

  function storeOffer(offer) {
    if (!offer.code || isExpired(offer)) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(offer));
    } catch (error) {}

    window.QLSLoadNativeCartOffer?.();
    document.dispatchEvent(new CustomEvent('qls:product-offer-activated', { detail: offer }));
  }

  function clearStoredOffer(code) {
    try {
      const current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!current || !code || current.code === code) sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  function formatRemaining(milliseconds) {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };
  }

  function setStatus(node, message, error = false) {
    const status = node.querySelector('[data-qls-product-offer-status]');
    if (!status) return;
    status.hidden = !message;
    status.textContent = message || '';
    status.style.color = error ? '#b42318' : '#087a16';
  }

  function expire(node, offer) {
    node.classList.add('is-expired');
    const expired = node.querySelector('[data-qls-product-offer-countdown-value]');
    if (expired) {
      expired.hidden = false;
      expired.textContent = node.dataset.expiredText || 'This offer has ended';
    }
    clearStoredOffer(offer.code);
  }

  function startCountdown(node, offer) {
    if (node.dataset.showCountdown !== 'true') return;

    const daysOutput = node.querySelector('[data-qls-product-offer-countdown-days]');
    const timeOutput = node.querySelector('[data-qls-product-offer-countdown-time]');
    const end = parseEnd(offer.endAt);

    if (!daysOutput || !timeOutput || end === null) return;

    let timer;

    const update = () => {
      const remaining = end - Date.now();

      if (remaining <= 0) {
        clearTimeout(timer);
        expire(node, offer);
        return;
      }

      const parts = formatRemaining(remaining);
      daysOutput.textContent = `${parts.days} ${parts.days === 1 ? 'day' : 'days'}`;
      timeOutput.textContent =
        `${String(parts.hours).padStart(2, '0')}:` +
        `${String(parts.minutes).padStart(2, '0')}:` +
        `${String(parts.seconds).padStart(2, '0')}`;

      timer = setTimeout(update, 1000);
    };

    update();
  }

  async function applyOffer(node, offer) {
    if (!offer.code || isExpired(offer)) {
      expire(node, offer);
      return;
    }

    const buttons = Array.from(node.querySelectorAll('[data-qls-product-offer-apply]'));

    storeOffer(offer);
    setStatus(node, '');

    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });

    try {
      const response = await fetch(route('/cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: offer.code })
      });

      if (!response.ok) throw new Error();

      setStatus(node, `Code ${offer.code} added to your cart.`);
      document.dispatchEvent(new CustomEvent('qls:discount-applied', {
        detail: { code: offer.code }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      setStatus(node, 'Final eligibility will be confirmed in your cart or at checkout.', true);
    } finally {
      buttons.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
    }
  }

  function init(node) {
    if (node.dataset.qlsProductOfferReady === 'true') return;
    node.dataset.qlsProductOfferReady = 'true';

    const primaryOffer = readPrimaryOffer(node);
    if (!primaryOffer.code) {
      node.hidden = true;
      return;
    }

    if (isExpired(primaryOffer)) {
      expire(node, primaryOffer);
      return;
    }

    if (node.dataset.autoSync === 'true') storeOffer(primaryOffer);
    startCountdown(node, primaryOffer);

    node.querySelectorAll('[data-qls-product-offer-apply]').forEach((button) => {
      button.addEventListener('click', () => {
        applyOffer(node, readButtonOffer(button, node));
      });
    });
  }

  function boot(root = document) {
    root.querySelectorAll?.('[data-qls-product-offer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
